// Chest block entity (M6.2). PURE (ENG-1).
//
// 27 slots, plus optional lazy loot: a generated (structure) chest carries a loot
// table name + position seed and rolls its contents the first time it is opened —
// so an untouched chest re-rolls deterministically from the seed if its chunk was
// evicted and regenerated, and only becomes concrete (and persisted) once a player
// looks inside (classic Minecraft loot-chest behaviour).
import { type ItemStack, type Slots, emptyStack, isEmpty, cloneStack } from '../inventory';
import { insert } from '../transaction';
import { rollLootByName, type LootOpts } from '../../entities/loot';
import { Rng } from '../../engine/rng';
import type { Registries } from '../../registry/load';

export const CHEST_SIZE = 27;

export interface ChestData {
  items: (ItemStack | null)[];
  /** Unresolved loot table name (structure chest not yet opened). */
  loot?: string;
  /** Deterministic roll seed for this chest's position. */
  lootSeed?: number;
}

export function newChest(): ChestData {
  return { items: Array.from({ length: CHEST_SIZE }, () => null) };
}

/** Non-empty stacks a broken chest scatters. */
export function chestContents(c: ChestData): ItemStack[] {
  return c.items.filter((s): s is ItemStack => s != null && !isEmpty(s));
}

/**
 * Realise a pending loot table into the chest (once). Scatters the rolled stacks
 * into empty slots using the position seed so the layout is deterministic. Clears
 * the loot fields so it never re-rolls, and is a no-op for a plain chest.
 *
 * `opts` forwards the host's loot seams — today the `explorer_map` minter, so a
 * shipwreck's map chest can hand out a map already pinned to buried treasure.
 */
export function fillChestLoot(c: ChestData, reg: Registries, opts: LootOpts = {}): void {
  if (!c.loot) return;
  const table = c.loot;
  const seed = c.lootSeed ?? 0;
  c.loot = undefined;
  c.lootSeed = undefined;
  const rng = new Rng(seed).fork('loot-chest');
  const stacks = rollLootByName(reg, table, rng, opts);
  const slots = chestSlots(c, (item) => reg.items.byName.get(reg.items.nameOf[item] ?? '')?.maxStack ?? 64);
  for (const stack of stacks) {
    if (isEmpty(stack)) continue;
    // Pick a random start slot, then let `insert` scan from there (wraps).
    const start = rng.nextInt(CHEST_SIZE);
    const order = Array.from({ length: CHEST_SIZE }, (_, i) => (start + i) % CHEST_SIZE);
    insert(slots, stack, order);
  }
}

/** A `Slots` view over a chest for the container window. */
export function chestSlots(c: ChestData, maxStackOf: (item: number) => number): Slots {
  return {
    size: CHEST_SIZE,
    get: (i) => c.items[i] ?? emptyStack(),
    set: (i, s) => { c.items[i] = isEmpty(s) ? null : s; },
    maxStackOf: (item) => maxStackOf(item),
    accepts: () => true,
  };
}

/** Deep-clone chest data (persistence snapshot). */
export function cloneChest(c: ChestData): ChestData {
  return {
    items: c.items.map((s) => (s && !isEmpty(s) ? cloneStack(s) : null)),
    loot: c.loot,
    lootSeed: c.lootSeed,
  };
}
